import { useState } from "react";
import { PencilSimpleLine, UploadSimple, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import DocumentPanel from "@/components/DocumentPanel";

const FIELDS = [
  ["background", "Background / Problem Statement"],
  ["purpose", "Purpose"],
  ["service_population", "Service Population"],
  ["deliverables", "Deliverables"],
  ["deadlines", "Deadlines"],
  ["geographic_scope", "Geographic / Service Scope"],
  ["performance_standards", "Standards of Performance"],
  ["performance_measures", "Measurable Outcomes"],
  ["monitoring_plan", "Monitoring / QA Plan"],
  ["reporting_method", "Reporting Method / Frequency"],
  ["reimbursement_instructions", "Reimbursement Instructions"],
  ["required_clauses", "Required Clauses / Disclaimers"],
];

export default function SowPanel({ contract, reload }) {
  const [mode, setMode] = useState(contract.sow?.mode || null);
  const [form, setForm] = useState(() => {
    const b = contract.sow?.builder || {};
    const init = {};
    FIELDS.forEach(([k]) => (init[k] = b[k] || ""));
    return init;
  });
  const [busy, setBusy] = useState(false);

  const saveBuilder = async () => {
    setBusy(true);
    try {
      await api.put(`/contracts/${contract.id}/sow/builder`, form);
      toast.success("SOW saved");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const linkUpload = async () => {
    const { data } = await api.get("/documents", { params: { related_type: "contract", related_id: contract.id, category: "SOW" } });
    if (!data.length) return toast.error("Upload a SOW document below first (category: SOW).");
    const latest = data[0];
    await api.put(`/contracts/${contract.id}/sow/upload`, { document_id: latest.id, version: latest.version, approved_final: true });
    toast.success(`SOW linked to ${latest.file_name}`);
    reload();
  };

  if (!mode) {
    return (
      <div className="grid sm:grid-cols-2 gap-4">
        <button data-testid="sow-choose-builder" onClick={() => setMode("builder")} className="border border-border rounded-md p-6 text-left hover:border-primary transition-colors bg-card">
          <PencilSimpleLine size={28} className="text-primary mb-3" />
          <div className="font-head font-semibold">Create SOW in Builder</div>
          <p className="text-sm text-muted-foreground mt-1">Complete a structured Scope of Work inside the app for standardized drafting and review.</p>
        </button>
        <button data-testid="sow-choose-upload" onClick={() => setMode("upload")} className="border border-border rounded-md p-6 text-left hover:border-primary transition-colors bg-card">
          <UploadSimple size={28} className="text-primary mb-3" />
          <div className="font-head font-semibold">Upload Existing SOW</div>
          <p className="text-sm text-muted-foreground mt-1">Attach an already approved or collaboratively drafted SOW in Word or PDF.</p>
        </button>
      </div>
    );
  }

  if (mode === "upload") {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-head font-semibold">Uploaded SOW</div>
            {contract.sow?.upload && (
              <div className="text-sm text-emerald-700 flex items-center gap-1.5 mt-1">
                <CheckCircle size={16} weight="fill" /> {contract.sow.upload.file_name} ({contract.sow.upload.version})
                {contract.sow.upload.approved_final && " · Approved final"}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" data-testid="sow-switch-builder" onClick={() => setMode("builder")}>Use builder instead</Button>
            <Button size="sm" data-testid="sow-link-upload" onClick={linkUpload}>Set latest SOW upload as final</Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Upload a file with category <b>SOW</b>, then click “Set latest SOW upload as final”.</p>
        <DocumentPanel relatedType="contract" relatedId={contract.id} onChange={reload} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div className="font-head font-semibold">SOW Builder</div>
        <Button size="sm" variant="ghost" data-testid="sow-switch-upload" onClick={() => setMode("upload")}>Upload instead</Button>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        {FIELDS.map(([k, label]) => (
          <div key={k} className={`space-y-1.5 ${["deliverables", "background", "purpose"].includes(k) ? "md:col-span-2" : ""}`}>
            <Label className="overline text-muted-foreground">{label}</Label>
            <Textarea data-testid={`sow-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="min-h-[72px]" />
          </div>
        ))}
      </div>
      <div className="mt-6">
        <Button data-testid="sow-save" onClick={saveBuilder} disabled={busy}>{busy ? "Saving…" : "Save SOW"}</Button>
      </div>
    </div>
  );
}
